import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, ArrowLeft, CheckCircle, Battery, Zap, Cpu, Clock, Shield, Globe } from 'lucide-react';
import Header from '../../components/layout/Header';
import Footer from '../../components/layout/Footer';
import { Button } from '../../components/ui/button';

const DeyePage = () => {
  const features = [
    {
      icon: Zap,
      title: "98.6% Max Efficiency",
      description: "Industry-leading conversion efficiency using T-type three-level topology and enhanced SVPWM algorithm."
    },
    {
      icon: Battery,
      title: "Hybrid Inverters",
      description: "Complete hybrid solutions supporting solar, battery, and grid integration in one compact unit."
    },
    {
      icon: Cpu,
      title: "Smart Monitoring",
      description: "Remote monitoring platform for parameter setting, firmware updates, and real-time system management."
    },
    {
      icon: Clock,
      title: "24h Support",
      description: "Round-the-clock technical support service to keep your system running optimally."
    }
  ];

  const products = [
    {
      name: "Three Phase Hybrid Inverter",
      model: "Sun 5/6/8/10/12K SG04LP3",
      features: [
        "100% unbalanced output",
        "48V low voltage battery",
        "Max 10 units in parallel",
        "240A max charging current",
        "6 time periods for battery management"
      ]
    },
    {
      name: "Single Phase String Inverter",
      model: "Sun 18/20/25/30/33K G04",
      features: [
        "Compatible with diesel generator",
        "Max DC input current of 16A",
        "Suited to 600W solar panels",
        "Type II SPD protection",
        "Frequency droop control"
      ]
    },
    {
      name: "Microinverter",
      model: "Various models",
      features: [
        "Panel-level optimization",
        "Easy installation",
        "Enhanced safety",
        "Individual monitoring",
        "Flexible system design"
      ]
    }
  ];

  const whyDeye = [
    "Founded in 2007 with over 15 years experience",
    "Over 30 million USD registered capital",
    "15,000m² manufacturing facility",
    "Complete production and testing equipment",
    "Global presence with local support",
    "VSG application support for poor grid areas"
  ];

  return (
    <div className="min-h-screen">
      <Header />
      <main>
        {/* Hero Section */}
        <section className="pt-32 pb-16 bg-gradient-to-br from-[#f8faf5] via-white to-[#f0f7e6] relative overflow-hidden">
          <div className="absolute top-20 right-10 w-72 h-72 bg-[#B5D43B]/10 rounded-full blur-3xl" />
          <div className="absolute bottom-20 left-10 w-96 h-96 bg-[#3D5A47]/5 rounded-full blur-3xl" />
          
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
            {/* Breadcrumb */}
            <Link to="/brands" className="inline-flex items-center gap-2 text-[#3D5A47] hover:text-[#B5D43B] mb-8 transition-colors">
              <ArrowLeft className="w-4 h-4" />
              <span className="text-sm font-medium">Back to Trusted Brands</span>
            </Link>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <div>
                <div className="flex items-center gap-2 mb-4">
                  <Battery className="w-5 h-5 text-[#B5D43B]" />
                  <span className="text-sm font-semibold text-[#B5D43B]">Hybrid Inverters & Batteries</span>
                </div>
                <h1 className="heading-1 mb-4">
                  <span className="text-[#B5D43B]">Deye</span>
                </h1>
                <p className="text-xl text-[#3D5A47] font-medium mb-4">
                  Professional PV Inverter Manufacturer
                </p>
                <p className="body-large text-gray-600 mb-8">
                  Ningbo Deye Inverter Technology Co., Ltd is a leading solar inverter manufacturer and hybrid system supplier. With cutting-edge technology and comprehensive solutions, Deye has become a major player in the global solar inverter market.
                </p>
                <div className="flex flex-wrap gap-4">
                  <Link to="/contact">
                    <Button className="btn-primary gap-2">
                      Get a Deye Quote
                      <ArrowRight className="w-4 h-4" />
                    </Button>
                  </Link>
                  <a href="https://au.deyeinverter.com/" target="_blank" rel="noopener noreferrer">
                    <Button className="btn-secondary gap-2">
                      Visit Deye Website
                      <Globe className="w-4 h-4" />
                    </Button>
                  </a>
                </div>
              </div>
              <div className="flex justify-center">
                <div className="bg-[#1a1a2e] p-8 rounded-2xl shadow-xl">
                  <img 
                    src="https://au.deyeinverter.com/template/eo/images/logo.png"
                    alt="Deye Logo"
                    className="w-64 h-auto"
                  />
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Key Features */}
        <section className="py-16 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="heading-2 text-center mb-12">Why Choose Deye?</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {features.map((feature, index) => (
                <div key={index} className="bg-gray-50 p-6 rounded-2xl hover:shadow-lg transition-shadow">
                  <div className="w-12 h-12 rounded-xl bg-[#B5D43B]/15 flex items-center justify-center mb-4">
                    <feature.icon className="w-6 h-6 text-[#3D5A47]" />
                  </div>
                  <h3 className="font-semibold text-[#3D5A47] mb-2">{feature.title}</h3>
                  <p className="text-gray-600 text-sm leading-relaxed">{feature.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Products */}
        <section className="py-16 bg-gray-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="heading-2 text-center mb-4">Product Range</h2>
            <p className="text-center text-gray-600 mb-12 max-w-2xl mx-auto">
              Deye offers a comprehensive range of inverters for residential, commercial, and utility applications.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {products.map((product, index) => (
                <div key={index} className="bg-white p-6 rounded-2xl border border-gray-100 shadow-sm">
                  <h3 className="font-bold text-[#3D5A47] mb-1">{product.name}</h3>
                  <p className="text-sm text-[#B5D43B] font-medium mb-4">{product.model}</p>
                  <ul className="space-y-2">
                    {product.features.map((feature, i) => (
                      <li key={i} className="flex items-start gap-2 text-sm text-gray-600">
                        <CheckCircle className="w-4 h-4 text-[#B5D43B] flex-shrink-0 mt-0.5" />
                        <span>{feature}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* About Deye */}
        <section className="py-16 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <div>
                <h2 className="heading-2 mb-6">About Deye</h2>
                <p className="text-gray-600 leading-relaxed mb-4">
                  Ningbo Deye Inverter Technology Co., Ltd is a professional PV inverter manufacturer founded in 2007 with registered capital of over 30 million USD. As one of China's high-tech enterprises and a subsidiary of Deye Group, they operate from a state-of-the-art 15,000m² facility.
                </p>
                <p className="text-gray-600 leading-relaxed mb-4">
                  Deye is dedicated to providing complete photovoltaic power system solutions, including residential and commercial power plant solutions. Their product range includes grid-connected inverters (1-136kW), hybrid inverters (3kW-50kW), and microinverters (300W-2000W).
                </p>
                <p className="text-gray-600 leading-relaxed">
                  As a technology-oriented company, Deye continuously invests in R&D to develop cutting-edge technologies that improve efficiency and reliability.
                </p>
              </div>
              <div className="space-y-3">
                <h3 className="font-semibold text-[#3D5A47] mb-4">Key Highlights</h3>
                {whyDeye.map((item, index) => (
                  <div key={index} className="flex items-center gap-3 p-4 bg-gray-50 rounded-xl">
                    <CheckCircle className="w-5 h-5 text-[#B5D43B] flex-shrink-0" />
                    <span className="text-gray-700">{item}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="py-16 bg-[#3D5A47]">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="heading-2 text-white mb-4">Ready for a Deye System?</h2>
            <p className="text-white/80 mb-8 max-w-2xl mx-auto">
              Let us help you design the perfect hybrid system using Deye's industry-leading inverter technology.
            </p>
            <Link to="/contact">
              <Button className="bg-[#B5D43B] hover:bg-[#a3c235] text-[#3D5A47] gap-2">
                Get Your Free Quote
                <ArrowRight className="w-4 h-4" />
              </Button>
            </Link>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default DeyePage;
